<template lang="pug">
Popover(v-model:open="isOpen")
  PopoverTrigger(as-child)
    Button.popbutton(
      :class="{ 'is-active': isActive }" 
      @native.click="$emit('action-click', 'image')")
      Icon(:name="icon" class="h-5 w-5")
  PopoverContent.p-2.image-popover.rounded-lg(
    side="top" 
    :sideOffset="Number(12)"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent="handleDragOver"
    @drop.prevent="handleDrop")
    .image-popover-content(:class="{ 'drag-active': isDragging }")
      LoadingOverlay.rounded-lg(:loading='uploadingImage')
        .button-wrapper.rounded-lg(class="bg-transparent dark:text-zinc-400")
          button.w-full.dropzone.flex.flex-col.items-center.justify-center.gap-2(
            @click="handleFileInputClick"
            :class="{ 'dragging': isDragging }")
            Icon(name="lucide:image-plus" class="h-8 w-8 mb-2 text-gray-600 dark:text-gray-400")
            p(class="text-xs text-gray-600 dark:text-gray-400") {{ isDragging ? $t('editor.drop_to_upload') : $t('editor.drop_or_click_upload') }}
        .url-input-group.flex.flex-row.items-center.gap-2.mt-2
          Input.image-url-input(
            v-model="localHrefForLink" 
            placeholder='Paste image URL'
            @keyup.enter="submitImageLink")
          Button.url-submit-btn.p-0(
            @click="submitImageLink"
            :disabled="!localHrefForLink"
            title="Use URL")
            Icon(name="lucide:check" class="h-5 w-5")
</template>

<script setup lang="ts">
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import LoadingOverlay from '@/components/ui/loading-overlay'
import { toast } from '~/utils/sonner'

const i18n = useI18n()
const $t = i18n.t

interface Props {
  icon: string
  isActive: boolean
  hrefForLink: string
  uploadingImage: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'action-click': [slug: string, option?: string]
  'use-image-link': [href: string]
  'upload-file': [file: File]
  'file-input-click': []
  'update:hrefForLink': [value: string]
}>()

// Watch for successful upload to close popover
watch(() => props.uploadingImage, (newVal, oldVal) => {
  // When uploadingImage changes from true to false, close the popover
  if (oldVal === true && newVal === false) {
    isOpen.value = false
  }
})

// Local state
const isDragging = ref(false)
const isOpen = ref(false)

// Two-way binding for hrefForLink
const localHrefForLink = computed({
  get: () => props.hrefForLink,
  set: (value) => emit('update:hrefForLink', value)
})

// Accepted image MIME types
const acceptedImageTypes = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp'
]

const isValidImageFile = (file: File) => {
  return acceptedImageTypes.includes(file.type)
}

// Drag event handlers
const handleDragEnter = (event: DragEvent) => {
  // Check if the dragged items contain at least one image file
  const items = event.dataTransfer?.items
  if (items) {
    for (let i = 0; i < items.length; i++) {
      if (items[i]?.kind === 'file') {
        const type = items[i]?.type
        if (type && acceptedImageTypes.includes(type)) {
          isDragging.value = true
          return
        }
      }
    }
  }
  // If no valid image files, don't activate drag state
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none'
  }
}

const handleDragOver = (event: DragEvent) => {
  // Check if we're accepting the drag
  const items = event.dataTransfer?.items
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item?.kind === 'file' && item?.type && acceptedImageTypes.includes(item.type)) {
        event.dataTransfer.dropEffect = 'copy'
        return
      }
    }
  }
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none'
  }
}

const handleDragLeave = (event: DragEvent) => {
  // Only turn off dragging if we're actually leaving the drop zone
  const relatedTarget = event.relatedTarget as Node
  const currentTarget = event.currentTarget as Node
  
  if (!currentTarget?.contains(relatedTarget)) {
    isDragging.value = false
  }
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    const file = files[0]
    if (file && isValidImageFile(file)) {
      emit('upload-file', file)
      // Close popover after successful drop
      isOpen.value = false
    } else {
      toast({
        title: $t('image_upload_fail.title') || 'Upload Failed',
        description: 'Please upload an image file (JPG, PNG, GIF, WebP)',
        variant: 'destructive',
        duration: 3000
      })
    }
  }
}

const submitImageLink = () => {
  if (localHrefForLink.value) {
    emit('use-image-link', localHrefForLink.value)
    // Close popover after successful submission
    isOpen.value = false
  }
}

const handleFileInputClick = () => {
  emit('file-input-click')
  // Note: Popover will be closed when uploadingImage changes from true to false
}
</script>

<style lang="postcss" scoped>
@reference "@/assets/css/tailwind.css";
.image-popover-content {
  @apply p-0 gap-y-3 rounded-lg;
  @apply transition-all duration-200;
  
  &.drag-active {
    @apply ring-ring ring-offset-1 bg-zinc-50/20;
    /* @apply scale-[1.01]; */
  }
  
  .dropzone {
    @apply w-full py-5 px-4 border-2 border-dashed border-gray-300 rounded-lg;
    @apply flex flex-col items-center justify-center cursor-pointer;
    @apply bg-gray-50 transition-all duration-200;
    @apply hover:border-gray-400 hover:bg-gray-100;
    
    &.dragging {
      @apply bg-blue-50 border-blue-400;
      
      :deep(svg) {
        @apply text-blue-600;
      }
      
      p {
        @apply text-blue-700 font-semibold;
      }
    }
  }
  
  .url-input-group {
    @apply flex items-center gap-2;
    
    .image-url-input {
      @apply flex-1 bg-gray-50 border-0 rounded-lg px-3 py-2 text-sm;
      @apply focus:outline-none focus:ring-2  placeholder:text-gray-400;
    }
    
    .url-submit-btn {
      @apply w-9 h-9 rounded-lg flex items-center justify-center !p-0;
      @apply text-white  transition-all duration-200;
      /* @apply active:scale-95; */
      
      &:disabled {
        @apply bg-gray-200 text-gray-400 cursor-not-allowed hover:bg-gray-200;
      }
    }
  }
}


/* Dark mode support */
.dark {
  :deep(.image-popover) {
    @apply bg-gray-800/95 text-white;
  }
  
  .image-popover-content {
    &.drag-active {
      @apply ring-2 ring-zinc-400 ring-offset-2 ring-offset-gray-800 bg-zinc-900/20;
    }
    
    .dropzone {
      @apply border-gray-600 bg-gray-700/50 hover:border-gray-500 hover:bg-gray-700;
      
      &.dragging {
        @apply bg-blue-900/30 border-blue-400;
        
        :deep(svg) {
          @apply text-blue-400;
        }
        
        p {
          @apply text-blue-300 font-semibold;
        }
      }
    }
    
    .url-input-group {
      .image-url-input {
        @apply bg-gray-700/50 text-white placeholder:text-gray-500;
        @apply focus:ring-zinc-400/20;
      }
      
      .url-submit-btn {
        @apply bg-zinc-600 hover:bg-zinc-700;
        
        &:disabled {
          @apply bg-gray-700 text-gray-500;
        }
      }
    }
  }
}
</style>